/**
 * The damage model — where a shell lands on a hull decides what it does.
 *
 * Shared and deterministic, like the rest of the simulation: the server is the
 * only one that *applies* it, but the browser reads the same state to draw
 * fires and flooding, and the tests replay it with a seeded PRNG. No clock, no
 * `Math.random()`, no I/O.
 *
 * Four zones, classified from the hit point in hull space:
 *
 * - `bow` / `stern` — the outer quarters of the length. Half damage, and each
 *   zone *saturates*: once it has absorbed `ZONE_SATURATION` of the ship's max
 *   HP, further hits there do a third. A ship cannot be sunk through its bow
 *   alone, which is what makes angling worth doing.
 * - `citadel` — the middle half of the length, below `CITADEL_DECK_FRACTION` of
 *   the deck height. Double damage, never saturates. Broadside at close range
 *   is the way in.
 * - `superstructure` — the middle half above that line. Reduced damage, and the
 *   place fires start.
 *
 * Status effects are rolled on the PRNG the caller hands in: fires on
 * superstructure hits, flooding on hull hits at or below the waterline. Both are
 * damage over time with a duration; flooding also slows the ship. The repair
 * party clears them all and heals a little over a few seconds — a limited
 * consumable, so *when* to use it is the decision.
 */
import type { Prng } from './prng';
import type { ShipSpec } from './ship-spec';

export type HitZone = 'bow' | 'stern' | 'citadel' | 'superstructure';

export interface FireState {
  /** Seconds left to burn. */
  remaining: number;
  /** Who started it, for the damage it does; absent for a fire nobody is owed. */
  byId?: string;
}

export interface FloodState {
  /** Seconds left before the crew has it under control on their own. */
  remaining: number;
  /** Who opened the hull, for the damage it does. */
  byId?: string;
}

/** Damage absorbed so far by each saturating zone, in HP. */
export interface ZonePool {
  bow: number;
  stern: number;
  super: number;
}

export interface RepairState {
  /** Uses left this life. */
  charges: number;
  /** Seconds until the party can be called again. */
  cooldown: number;
  /** Seconds of healing still to be paid out from the last use. */
  healingRemaining: number;
}

export interface DamageState {
  fires: FireState[];
  flooding: FloodState | null;
  zonePool: ZonePool;
  repair: RepairState;
}

// ─── Tuning ──────────────────────────────────────────────────────────────────

export const ZONE_MULTIPLIER: Readonly<Record<HitZone, number>> = {
  bow: 0.5,
  stern: 0.5,
  citadel: 2.0,
  superstructure: 0.6,
};
/** Damage a saturated zone still takes, as a fraction of its normal share. */
export const SATURATED_MULTIPLIER = 0.33;
/** Fraction of max HP a bow, stern or superstructure can absorb before saturating. */
export const ZONE_SATURATION = 0.25;
/** Middle-zone hits below this fraction of the deck height are citadel hits. */
export const CITADEL_DECK_FRACTION = 0.6;
/** Hull hits at or below this fraction of the deck height can flood. */
export const WATERLINE_DECK_FRACTION = 0.3;
/** The outer quarters of the length are bow and stern. */
export const END_ZONE_FRACTION = 0.25;

export const FIRE_CHANCE = 0.12;
export const MAX_FIRES = 2;
export const FIRE_DURATION = 30;
/** Fraction of max HP burnt per second, per fire. */
export const FIRE_DPS_FRACTION = 0.003;

export const FLOOD_CHANCE = 0.25;
/**
 * A torpedo is a hole below the waterline, so flooding is the usual outcome
 * rather than the lucky one. Three in four keeps it short of a certainty —
 * a captain who eats a fish and stays dry has had a break, not a bug.
 */
export const TORPEDO_FLOOD_CHANCE = 0.75;
export const FLOOD_DURATION = 40;
/** Fraction of max HP lost per second while flooding. */
export const FLOOD_DPS_FRACTION = 0.005;
/** Top speed while flooding, as a fraction of normal. */
export const FLOOD_SPEED_FACTOR = 0.8;

export const REPAIR_CHARGES = 3;
export const REPAIR_COOLDOWN = 90;
export const REPAIR_HEAL_FRACTION = 0.1;
export const REPAIR_HEAL_DURATION = 10;

// ─── State ───────────────────────────────────────────────────────────────────

/** Charges the repair party starts a life with: the spec's, or the stock three. */
export function repairChargesFor(spec?: Pick<ShipSpec, 'repairCharges'>): number {
  const n = spec?.repairCharges;
  return typeof n === 'number' && n > 0 ? Math.floor(n) : REPAIR_CHARGES;
}

export function initialDamageState(spec?: Pick<ShipSpec, 'repairCharges'>): DamageState {
  return {
    fires: [],
    flooding: null,
    zonePool: { bow: 0, stern: 0, super: 0 },
    repair: { charges: repairChargesFor(spec), cooldown: 0, healingRemaining: 0 },
  };
}

/** A deep copy, so a stepped state never shares arrays with the one before. */
export function cloneDamageState(d: DamageState): DamageState {
  return {
    fires: d.fires.map((f) => ({ ...f })),
    flooding: d.flooding ? { ...d.flooding } : null,
    zonePool: { ...d.zonePool },
    repair: { ...d.repair },
  };
}

// ─── Hits ────────────────────────────────────────────────────────────────────

/** A hit point in hull space: `f` along the keel toward the bow, `y` up from the waterline. */
export interface LocalHit {
  f: number;
  y: number;
}

export function classifyHit(hit: LocalHit, spec: ShipSpec): HitZone {
  const endStart = spec.hull.halfLength * (1 - 2 * END_ZONE_FRACTION);
  if (hit.f > endStart) return 'bow';
  if (hit.f < -endStart) return 'stern';
  return hit.y < spec.hull.height * CITADEL_DECK_FRACTION ? 'citadel' : 'superstructure';
}

export interface ShellHitResult {
  damage: DamageState;
  /** HP actually removed. */
  amount: number;
  zone: HitZone;
  fireStarted: boolean;
  floodStarted: boolean;
}

/**
 * What kind of hit this is. Zones, saturation and the status effects are one
 * model with two settings, not two models: a shell and a torpedo differ in
 * where they land and what that is worth, and nowhere else.
 */
export interface HitProfile {
  /** Damage multiplier per zone. */
  zone: Readonly<Record<HitZone, number>>;
  /** Chance a superstructure hit starts a fire. */
  fireChance: number;
  /** Chance a hit at or below the waterline lets water in. */
  floodChance: number;
  /**
   * Whether a hit amidships can flood. A shell that reaches the citadel has
   * come in over the belt and the crew is already fighting a fire, not a leak;
   * a torpedo arrives under it, and amidships is exactly where it hurts.
   */
  floodAmidships: boolean;
}

export const SHELL_HIT: Readonly<HitProfile> = {
  zone: ZONE_MULTIPLIER,
  fireChance: FIRE_CHANCE,
  floodChance: FLOOD_CHANCE,
  floodAmidships: false,
};

/**
 * A torpedo strikes the belt below the waterline, where a shell's citadel
 * bonus has no meaning — the warhead is the warhead wherever it goes off. So
 * the zones only still say *which end*: the bow and the stern take half and
 * saturate as they do for gunfire, and amidships is flat.
 *
 * That flatness is the balance of the weapon. A destroyer's fish does 5 500 to
 * a cruiser's 28 000 hull, not 11 000; three of them and the flooding they
 * bring is a fight-ending salvo, which is the point, but it is not a cruiser
 * deleted by one lucky fan.
 */
export const TORPEDO_ZONE_MULTIPLIER: Readonly<Record<HitZone, number>> = {
  bow: 0.5,
  stern: 0.5,
  citadel: 1,
  superstructure: 1,
};

export const TORPEDO_HIT: Readonly<HitProfile> = {
  zone: TORPEDO_ZONE_MULTIPLIER,
  fireChance: 0,
  floodChance: TORPEDO_FLOOD_CHANCE,
  floodAmidships: true,
};

/**
 * Resolve one shell hit against `damage`, returning the new damage state and
 * what happened. `hp` is not touched here — the caller subtracts `amount`, so
 * the same function serves the arena and the tests.
 */
export function resolveShellHit(
  damage: DamageState,
  hit: LocalHit,
  baseDamage: number,
  spec: ShipSpec,
  rng: Prng,
  byId?: string,
): ShellHitResult {
  return resolveHit(damage, hit, baseDamage, spec, rng, SHELL_HIT, byId);
}

/**
 * Resolve one torpedo hit. Same model, torpedo settings: a large hit that
 * usually floods, and never a fire — there is nothing burning four metres
 * under the waterline.
 */
export function resolveTorpedoHit(
  damage: DamageState,
  hit: LocalHit,
  baseDamage: number,
  spec: ShipSpec,
  rng: Prng,
  byId?: string,
): ShellHitResult {
  return resolveHit(damage, hit, baseDamage, spec, rng, TORPEDO_HIT, byId);
}

export function resolveHit(
  damage: DamageState,
  hit: LocalHit,
  baseDamage: number,
  spec: ShipSpec,
  rng: Prng,
  profile: Readonly<HitProfile> = SHELL_HIT,
  /** The shooter, remembered on any fire or flood this hit starts. */
  byId?: string,
): ShellHitResult {
  const next = cloneDamageState(damage);
  const zone = classifyHit(hit, spec);
  let amount = baseDamage * profile.zone[zone];

  const poolKey: keyof ZonePool | null = zone === 'bow' ? 'bow' : zone === 'stern' ? 'stern' : zone === 'superstructure' ? 'super' : null;
  if (poolKey) {
    const cap = spec.hp * ZONE_SATURATION;
    const absorbed = next.zonePool[poolKey];
    if (absorbed >= cap) {
      amount *= SATURATED_MULTIPLIER;
    } else if (absorbed + amount > cap) {
      // Straddles the cap: the remainder of the pool at full rate, the rest saturated.
      const full = cap - absorbed;
      amount = full + (amount - full) * SATURATED_MULTIPLIER;
    }
    next.zonePool[poolKey] = absorbed + amount;
  }
  amount = Math.round(amount);

  let fireStarted = false;
  let floodStarted = false;
  if (zone === 'superstructure') {
    // Roll whether or not a slot is free: the PRNG sequence must not depend on
    // how many fires already burn, or the replay drifts.
    const roll = rng.next();
    if (roll < profile.fireChance && next.fires.length < MAX_FIRES) {
      next.fires.push({ remaining: FIRE_DURATION * (spec.statusDurationMult ?? 1), ...(byId ? { byId } : {}) });
      fireStarted = true;
    }
  } else if (
    (profile.floodAmidships || zone === 'bow' || zone === 'stern') &&
    hit.y <= spec.hull.height * WATERLINE_DECK_FRACTION
  ) {
    const roll = rng.next();
    if (roll < profile.floodChance && next.flooding === null) {
      next.flooding = { remaining: FLOOD_DURATION * (spec.statusDurationMult ?? 1), ...(byId ? { byId } : {}) };
      floodStarted = true;
    }
  }

  return { damage: next, amount, zone, fireStarted, floodStarted };
}

// ─── Per tick ────────────────────────────────────────────────────────────────

/** One source of damage over time and what it did this tick. */
export interface DotCredit {
  byId: string | null;
  amount: number;
}

export interface DamageTickResult {
  damage: DamageState;
  /** HP burnt this tick. */
  fireDamage: number;
  /** HP flooded this tick. */
  floodDamage: number;
  /** The burning, fire by fire, with who lit each; sums to `fireDamage`. */
  burns: DotCredit[];
  /** The flooding and who opened the hull; `null` when dry. */
  flood: DotCredit | null;
  /** HP restored this tick by a running repair. */
  healed: number;
  /** Fires that burnt out this tick. */
  firesOut: number;
  floodOut: boolean;
}

/**
 * Advance fires, flooding, the repair cooldown and any running heal by `dt`.
 * Pure; the caller applies the HP deltas (damage first, then healing, so a heal
 * cannot revive a hull that just went under).
 */
export function stepDamage(damage: DamageState, spec: ShipSpec, dt: number): DamageTickResult {
  const next = cloneDamageState(damage);
  let fireDamage = 0;
  let firesOut = 0;
  const fires: FireState[] = [];
  const burns: DotCredit[] = [];
  for (const f of next.fires) {
    const burn = Math.min(dt, f.remaining);
    const amount = spec.hp * FIRE_DPS_FRACTION * burn;
    fireDamage += amount;
    burns.push({ byId: f.byId ?? null, amount });
    const remaining = f.remaining - dt;
    if (remaining > 0) fires.push({ remaining, ...(f.byId ? { byId: f.byId } : {}) });
    else firesOut++;
  }
  next.fires = fires;

  let floodDamage = 0;
  let floodOut = false;
  let flood: DotCredit | null = null;
  if (next.flooding) {
    const wet = Math.min(dt, next.flooding.remaining);
    floodDamage += spec.hp * FLOOD_DPS_FRACTION * wet;
    flood = { byId: next.flooding.byId ?? null, amount: floodDamage };
    const remaining = next.flooding.remaining - dt;
    if (remaining > 0) next.flooding = { remaining, ...(next.flooding.byId ? { byId: next.flooding.byId } : {}) };
    else {
      next.flooding = null;
      floodOut = true;
    }
  }

  let healed = 0;
  if (next.repair.healingRemaining > 0) {
    const heal = Math.min(dt, next.repair.healingRemaining);
    healed = (spec.hp * REPAIR_HEAL_FRACTION * heal) / REPAIR_HEAL_DURATION;
    next.repair.healingRemaining = Math.max(0, next.repair.healingRemaining - dt);
  }
  next.repair.cooldown = Math.max(0, next.repair.cooldown - dt);

  return { damage: next, fireDamage, floodDamage, burns, flood, healed, firesOut, floodOut };
}

// ─── Repair party ────────────────────────────────────────────────────────────

export function canRepair(damage: DamageState): boolean {
  return damage.repair.charges > 0 && damage.repair.cooldown <= 0;
}

/**
 * Call the repair party: fires out, flooding stopped, a heal started, one
 * charge spent. Returns the same state when it cannot be called.
 */
export function activateRepair(damage: DamageState): DamageState {
  if (!canRepair(damage)) return damage;
  return {
    fires: [],
    flooding: null,
    zonePool: { ...damage.zonePool },
    repair: {
      charges: damage.repair.charges - 1,
      cooldown: REPAIR_COOLDOWN,
      healingRemaining: REPAIR_HEAL_DURATION,
    },
  };
}

/** Top speed for a ship in this condition. */
export function speedFactor(damage: DamageState): number {
  return damage.flooding ? FLOOD_SPEED_FACTOR : 1;
}

/** The compact form a snapshot carries. */
export interface DamageSnapshot {
  fires: number[];
  flooding: number | null;
  repair: RepairState;
}

export function damageSnapshot(d: DamageState): DamageSnapshot {
  return {
    fires: d.fires.map((f) => f.remaining),
    flooding: d.flooding ? d.flooding.remaining : null,
    repair: { ...d.repair },
  };
}

/** Rebuild a damage state from a snapshot; the zone pools are the server's business and start empty. */
export function damageFromSnapshot(s: DamageSnapshot | undefined, previous?: DamageState): DamageState {
  if (!s) return previous ? cloneDamageState(previous) : initialDamageState();
  return {
    fires: s.fires.map((remaining) => ({ remaining })),
    flooding: s.flooding === null ? null : { remaining: s.flooding },
    zonePool: previous ? { ...previous.zonePool } : { bow: 0, stern: 0, super: 0 },
    repair: { ...s.repair },
  };
}
